import { useCallback, useRef } from 'react';
import { v4 } from 'uuid';
import { encode } from 'base64-arraybuffer';
import { imageSize } from '../lib/metrics';
import { log } from '../lib/logger';
import { i18n } from '../i18n';
import { useLineOfBusiness } from './useLineOfBusiness';
import { useUploadFileRequest } from '../api/useUploadFileRequest';
import { allowedUploadFileExtensions } from '../constants/acceptedUploadFileExtensions';
import { docxPreviewImage, pdfPreviewImage, pptxPreviewImage, xlsxPreviewImage, } from '../constants/previewImageFileUrls';
const getBase64 = (file) => file.arrayBuffer().then(content => encode(content));
export const useGetHandleFileUpload = () => {
    const uploadFileRequest = useUploadFileRequest();
    return useCallback((file, caption) => {
        var _a;
        const id = v4();
        getBase64(file).then(content => {
            return uploadFileRequest({
                id,
                caption,
                fileName: file.name,
                fileContent: content,
                timestamp: Date.now().toString(),
            });
        });
        const extension = (_a = file.name.split('.').pop()) === null || _a === void 0 ? void 0 : _a.toLowerCase();
        let attachmentLink = URL.createObjectURL(file);
        if (extension === 'docx') {
            attachmentLink = docxPreviewImage;
        }
        else if (extension === 'pptx') {
            attachmentLink = pptxPreviewImage;
        }
        else if (extension === 'xlsx') {
            attachmentLink = xlsxPreviewImage;
        }
        else if (extension === 'pdf') {
            attachmentLink = pdfPreviewImage;
        }
        return {
            id,
            caption,
            attachmentLink,
        };
    }, [uploadFileRequest]);
};
export const useGetHandleFileChange = (onError) => {
    const { lineOfBusiness } = useLineOfBusiness();
    return useCallback((e) => {
        var _a, _b, _c;
        if (((_a = e.target.files) === null || _a === void 0 ? void 0 : _a.length) !== 1) {
            onError(i18n.t('FILE_NOT_SELECTED_TEXT'));
            return;
        }
        const file = (_b = e.target.files) === null || _b === void 0 ? void 0 : _b[0];
        imageSize(lineOfBusiness, file.size).catch(log.error);
        if (file.size > 5000000) {
            onError(i18n.t('FILE_TOO_BIG_TEXT'));
            return;
        }
        const extension = (_c = file.name.split('.').pop()) === null || _c === void 0 ? void 0 : _c.toLowerCase();
        if (!allowedUploadFileExtensions.includes(extension !== null && extension !== void 0 ? extension : '')) {
            onError(`Sorry, but the file type .${extension} is not allowed.`);
            return;
        }
        if (extension === 'docx') {
            return docxPreviewImage;
        }
        else if (extension === 'pptx') {
            return pptxPreviewImage;
        }
        else if (extension === 'xlsx') {
            return xlsxPreviewImage;
        }
        else if (extension === 'pdf') {
            return pdfPreviewImage;
        }
        return URL.createObjectURL(file);
    }, [lineOfBusiness, onError]);
};
export const useUploadFile = ({ onError }) => {
    const inputRef = useRef(null);
    const handleUploadFile = useGetHandleFileUpload();
    const handleFileChange = useGetHandleFileChange(onError);
    const handleAttachmentClick = useCallback(() => {
        var _a;
        (_a = inputRef === null || inputRef === void 0 ? void 0 : inputRef.current) === null || _a === void 0 ? void 0 : _a.click();
    }, []);
    return {
        inputRef,
        handleFileChange,
        handleAttachmentClick,
        handleUploadFile,
    };
};
